import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Paper, 
  Chip, 
  Divider, 
  Button, 
  Stack
} from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LinkIcon from '@mui/icons-material/Link';
import MemoryIcon from '@mui/icons-material/Memory';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

function Technology() {
  const techFeatures = [
    { icon: <VerifiedUserIcon />, title: 'Blockchain Provenance', desc: '' },
    { icon: <FingerprintIcon />, title: 'Unique Digital Identity', desc: '' },
    { icon: <MemoryIcon />, title: 'Smart Contract Escrow', desc: '' },
    { icon: <LinkIcon />, title: 'Cross-Platform APIs', desc: '' },
  ];

  return (
    <Box sx={{ backgroundColor: '#050505', minHeight: '100vh', overflowX: 'hidden' }}>
      
      {/* ============ HERO SECTION ============ */}
      <Box
        sx={{
          position: 'relative',
          height: '70vh',
          minHeight: '550px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
          backgroundImage: 'url(/images/hero.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {/* Dark Overlay */}
        <Box sx={{ 
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.75) 50%, rgba(0, 0, 0, 0.95) 100%)', 
          zIndex: 1 
        }} />
        
        {/* Slow Zoom */}
        <Box sx={{ 
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
          backgroundImage: 'url(/images/hero.png)', backgroundSize: 'cover', backgroundPosition: 'center', 
          animation: 'slowZoom 25s ease-in-out infinite alternate', 
          '@keyframes slowZoom': { '0%': { transform: 'scale(1)' }, '100%': { transform: 'scale(1.1)' } }, 
          zIndex: 0 
        }} />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 10, pt: { xs: '140px', md: '180px' } }}>
          
          <Typography variant="h1" component="h1" sx={{
            fontFamily: "'Playfair Display', serif", fontWeight: 600, fontSize: { xs: '3rem', sm: '3.5rem', md: '5rem' }, lineHeight: 1.1, mb: 4,
            background: 'linear-gradient(90deg, #ffffff 0%, #d4af37 50%, #ffffff 100%)', backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', animation: 'shimmer 6s linear infinite',
            filter: 'drop-shadow(0 4px 15px rgba(0,0,0,0.8))',
            '@keyframes shimmer': { '0%': { backgroundPosition: '200% center' }, '100%': { backgroundPosition: '-200% center' } },
          }}>
            Our Technology
          </Typography>

          <Typography variant="body1" sx={{ color: '#e0e0e0', fontSize: { xs: '1rem', md: '1.2rem' }, fontWeight: 300, maxWidth: '700px', mx: 'auto', mb: 5, lineHeight: 1.8, textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
            We utilize blockchain verification and secure digital ledgers to ensure the absolute authenticity, provenance, and security of every asset. 
            Smart contracts automate verification for seamless stablecoin operations.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button component={RouterLink} to="/contact" variant="contained" endIcon={<ArrowForwardIcon />} sx={{
              background: 'linear-gradient(135deg, #d4af37 0%, #b8962f 100%)', 
              color: '#000000', 
              fontWeight: 800, 
              fontSize: '0.85rem', 
              letterSpacing: '2px', 
              textTransform: 'uppercase', 
              padding: '18px 40px', 
              borderRadius: '4px', 
              boxShadow: '0 10px 40px rgba(212, 175, 55, 0.4)', 
              transition: 'all 0.5s ease',
              '&:hover': { 
                background: 'linear-gradient(135deg, #e8c84a 0%, #d4af37 100%)', 
                color: '#000000', 
                boxShadow: '0 15px 50px rgba(212, 175, 55, 0.5)', 
                transform: 'translateY(-3px)' 
              },
            }}>
              Get Started
            </Button>
            <Button component={RouterLink} to="/developers" variant="outlined" sx={{
              borderColor: '#d4af37', 
              color: '#d4af37', 
              fontWeight: 700, 
              fontSize: '0.85rem', 
              letterSpacing: '2px', 
              textTransform: 'uppercase', 
              padding: '18px 40px', 
              borderRadius: '4px',
              '&:hover': { borderColor: '#ffffff', color: '#ffffff', backgroundColor: 'rgba(212, 175, 55, 0.1)' },
            }}>
              View Developer Docs
            </Button>
          </Stack>
        </Container>
      </Box>
      {/* ============ END HERO ============ */}

      {/* ============ MAIN CONTENT ============ */}
      <Container maxWidth="xl" sx={{ py: { xs: 8, md: 12 } }}>
        
        {/* Tech Feature Cards - All on one row */}
        <Grid container spacing={2.5} sx={{ mb: 12, flexWrap: 'nowrap' }}>
          {techFeatures.map((item, index) => (
            <Grid item xs={3} key={index} sx={{ display: 'flex', minWidth: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  background: 'linear-gradient(135deg, #141414 0%, #0a0a0a 100%)',
                  border: '1px solid rgba(212, 175, 55, 0.15)',
                  borderRadius: '6px',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.4s ease',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '50%',
                    background: 'linear-gradient(to bottom, rgba(255,255,255,0.05), transparent)',
                    pointerEvents: 'none',
                  },
                  '&:hover': {
                    borderColor: '#d4af37',
                    transform: 'translateY(-5px)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.8), 0 0 20px rgba(212, 175, 55, 0.2)',
                    '& .tech-icon-box': {
                      borderColor: '#d4af37',
                      color: '#d4af37',
                      backgroundColor: 'rgba(212, 175, 55, 0.1)',
                    }
                  },
                }}
              >
                <Box 
                  className="tech-icon-box"
                  sx={{ 
                    color: '#d4af37', 
                    height: '50px', 
                    width: '50px',
                    mb: 2, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    borderRadius: '4px',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    backgroundColor: 'rgba(212, 175, 55, 0.05)',
                    transition: 'all 0.4s ease',
                    '& svg': { fontSize: '2rem' } 
                  }}
                >
                  {item.icon}
                </Box>

                <Typography variant="h6" sx={{ 
                  fontFamily: "'Playfair Display', serif", 
                  color: '#ffffff', 
                  mb: 1.5, 
                  fontSize: '1.1rem',
                  height: '28px'
                }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: '#a1a1a1', 
                  lineHeight: 1.7, 
                  fontSize: '0.85rem',
                  flexGrow: 1
                }}>
                  {item.desc}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Live Blockchain Visualizer */}
        <Box>
          <Typography variant="h3" sx={{ 
            fontFamily: "'Playfair Display', serif", 
            color: '#fff', 
            textAlign: 'center', 
            mb: 6,
            fontSize: { xs: '2rem', md: '2.5rem' }
          }}>
            Ledger Verification
          </Typography>
          
          <Paper
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #111111 0%, #050505 100%)',
              border: '1px solid rgba(212, 175, 55, 0.3)',
              borderRadius: '8px',
              p: { xs: 4, md: 6 },
              maxWidth: '800px',
              mx: 'auto',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8), 0 0 30px rgba(212, 175, 55, 0.1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-50%',
                left: '-50%',
                width: '200%',
                height: '200%',
                background: 'radial-gradient(circle, rgba(212,175,55,0.08) 0%, transparent 60%)',
              },
            }}
          >
            {/* Status Bar */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, position: 'relative', zIndex: 1, flexWrap: 'wrap', gap: 2 }}>
              <Chip 
                label="BLOCKCHAIN ACTIVE" 
                size="small"
                sx={{ 
                  bgcolor: 'rgba(212, 175, 55, 0.15)', 
                  color: '#d4af37', 
                  fontWeight: 700, 
                  letterSpacing: '2px',
                  borderRadius: '4px',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  '& .MuiChip-label': { fontSize: '0.7rem' }
                }} 
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  size="small"
                  label="Ethereum"
                  sx={{
                    bgcolor: 'rgba(165, 164, 164, 0.05)',
                    color: '#a1a1a1',
                    fontWeight: 600,
                    fontSize: '0.6rem',
                    borderRadius: '4px',
                    border: '1px solid rgba(165, 164, 164, 0.2)',
                  }}
                />
                <Typography variant="caption" sx={{ color: '#a1a1a1', fontFamily: 'monospace' }}>
                  Block #73,551,202
                </Typography>
              </Box>
            </Box>

            {/* Ledger Entries */}
            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {[
                { id: '0x9f2a...c19d', item: 'Diamond · DIA-2026', status: 'VERIFIED' },
                { id: '0x7a1b...5f3e', item: 'Gold Bar · GB-1000g', status: 'VERIFIED' },
                { id: '0x1c8d...a0b2', item: 'Smart Contract · SC-114', status: 'ACTIVE' },
              ].map((block, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    p: 3, 
                    background: 'linear-gradient(90deg, rgba(212, 175, 55, 0.03) 0%, rgba(212, 175, 55, 0.06) 100%)',
                    border: '1px solid rgba(212, 175, 55, 0.15)', 
                    borderRadius: '6px',
                    flexWrap: 'wrap',
                    gap: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#d4af37',
                      transform: 'translateX(5px)',
                    }
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#61afef', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {block.id}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#d4af37', fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 500 }}>
                    {block.item}
                  </Typography>
                  <Chip
                    size="small"
                    label={block.status}
                    sx={{
                      bgcolor: 'rgba(212, 175, 55, 0.1)',
                      color: '#d4af37',
                      fontWeight: 700,
                      fontSize: '0.6rem',
                      borderRadius: '4px',
                      border: '1px solid rgba(212, 175, 55, 0.2)',
                    }}
                  />
                </Box>
              ))}
            </Box>

            <Divider sx={{ borderColor: 'rgba(212, 175, 55, 0.15)', my: 4, position: 'relative', zIndex: 1 }} />
            
            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="caption" sx={{ color: '#a1a1a1', letterSpacing: '1px' }}>
                Immutable · End-to-End Encrypted · Zero-Knowledge Proofs
              </Typography>
            </Box>
          </Paper>
        </Box>

      </Container>
    </Box>
  );
}

export default Technology;